"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format, isSameDay, subDays } from "date-fns";
import {
  Calendar as CalendarIcon,
  Bed,
  Moon,
  Sun,
  Baby,
  Milk,
  Plus,
  Trash2,
  Check,
  Layers,
  AlertCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { NewSleepEvent, SleepEventType, SleepGoal, SleepEvent } from "@/lib/types";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { TimePicker } from "./time-picker";
import {
  NapDurationPicker,
  addMinutesToTimeString,
  calculateDurationMinutes,
  formatDurationLabel,
} from "./nap-duration-picker";
import { Timestamp } from "firebase/firestore";
import { useIsMobile } from "@/hooks/use-mobile";
import { Capacitor } from "@capacitor/core";

const pad = (num: number) => num.toString().padStart(2, "0");

const createFormSchema = (isEditing: boolean) =>
  z
    .object({
      date: z.date({ required_error: "A date is required." }),
      time: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format (HH:mm)"),
      endTime: z
        .string()
        .optional()
        .refine((val) => {
          if (!val || val.trim() === "") return true;
          return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(val);
        }, "Invalid time format (HH:mm)"),
      type: z.custom<SleepEventType>((val) => val, "Please select an event type."),
      duration: z.coerce.number().min(0, "Duration must be positive.").optional(),
      notes: z.string().optional(),
    })
    .refine(
      (data) => {
        // If type is 'nap' and NOT editing, endTime is required
        if (!isEditing && data.type === "nap") {
          return !!(data.endTime && data.endTime.trim() !== "");
        }
        return true;
      },
      {
        message: "End time is required for new nap events",
        path: ["endTime"],
      }
    )
    .refine(
      (data) => {
        // If type is 'nap' and endTime is provided, it must be after start time
        if (data.type === "nap" && data.endTime && data.endTime.trim() !== "" && data.time) {
          const [startHours, startMinutes] = data.time.split(":").map(Number);
          const [endHours, endMinutes] = data.endTime.split(":").map(Number);

          const startMinutesTotal = startHours * 60 + startMinutes;
          const endMinutesTotal = endHours * 60 + endMinutes;

          return endMinutesTotal > startMinutesTotal;
        }
        return true;
      },
      {
        message: "End time must be after start time",
        path: ["endTime"],
      }
    );

type SleepLogDialogProps = {
  onSave: (data: Omit<NewSleepEvent, "childId">) => void;
  onSaveBatch?: (events: Omit<NewSleepEvent, "childId">[]) => void;
  onEdit?: (eventId: string, data: Omit<NewSleepEvent, "childId">) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  goal?: SleepGoal;
  eventToEdit?: SleepEvent;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialMode?: "single" | "batch";
};

interface BatchNapRow {
  id: string;
  startTime: string;
  endTime: string;
  notes: string;
}

const eventTypes: { type: SleepEventType; label: string; icon: React.ReactNode }[] = [
  { type: "bedtime", label: "Bedtime", icon: <Moon className="h-4 w-4" /> },
  { type: "wake", label: "Wake", icon: <Sun className="h-4 w-4" /> },
  { type: "nap", label: "Nap", icon: <Bed className="h-4 w-4" /> },
  { type: "night_wake", label: "Night Wake", icon: <Baby className="h-4 w-4" /> },
  { type: "feed", label: "Feed", icon: <Milk className="h-4 w-4" /> },
];

const getRoundedTime = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  const roundedMinutes = Math.floor(minutes / 5) * 5;
  now.setMinutes(roundedMinutes);
  return format(now, "HH:mm");
};

export function SleepLogDialog({
  onSave,
  onSaveBatch,
  onEdit,
  children,
  eventToEdit,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  initialMode = "single",
}: SleepLogDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [batchDatePickerOpen, setBatchDatePickerOpen] = useState(false);
  const [savedFeedback, setSavedFeedback] = useState<string | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [mode, setMode] = useState<"single" | "batch">(initialMode);

  // Batch naps state
  const [batchDate, setBatchDate] = useState<Date>(new Date());
  const [batchNaps, setBatchNaps] = useState<BatchNapRow[]>([
    { id: "1", startTime: "09:30", endTime: "10:15", notes: "" },
    { id: "2", startTime: "13:00", endTime: "14:15", notes: "" },
  ]);

  const isMobile = useIsMobile();
  const isIOS = Capacitor.getPlatform() === "ios";

  const open = controlledOpen ?? internalOpen;
  const setOpen = setControlledOpen ?? setInternalOpen;
  const isEditing = !!eventToEdit;

  const formSchema = createFormSchema(isEditing);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: new Date(),
      time: getRoundedTime(),
      endTime: addMinutesToTimeString(getRoundedTime(), 60),
      type: "bedtime",
      notes: "",
      duration: 15,
    },
  });

  const eventType = form.watch("type");
  const formTime = form.watch("time");
  const formEndTime = form.watch("endTime");
  const formDate = form.watch("date");

  useEffect(() => {
    if (open) {
      setSavedFeedback(null);
      setBatchError(null);

      if (eventToEdit) {
        setMode("single");
        if (!eventToEdit.time) {
          console.warn("Event to edit has no time:", eventToEdit);
          return;
        }

        let eventDate: Date;
        try {
          eventDate = (eventToEdit.time as Timestamp).toDate();
          if (isNaN(eventDate.getTime())) {
            console.warn("Invalid date for event to edit:", eventToEdit);
            return;
          }
        } catch (error) {
          console.warn("Error converting timestamp for event to edit:", eventToEdit, error);
          return;
        }

        const mappedType: SleepEventType =
          eventToEdit.type === "nap_start" || eventToEdit.type === "nap_end"
            ? "nap"
            : eventToEdit.type;

        form.reset({
          date: eventDate,
          time: format(eventDate, "HH:mm"),
          endTime: "",
          type: mappedType,
          notes: eventToEdit.notes || "",
          duration: eventToEdit.duration || 15,
        });
      } else {
        setMode(initialMode);
        const roundedNow = getRoundedTime();
        form.reset({
          date: new Date(),
          time: roundedNow,
          endTime: addMinutesToTimeString(roundedNow, 60),
          type: "nap",
          notes: "",
          duration: 15,
        });
        setBatchDate(new Date());
        setBatchNaps([
          { id: "1", startTime: "09:30", endTime: "10:15", notes: "" },
          { id: "2", startTime: "13:00", endTime: "14:15", notes: "" },
        ]);
      }
    }
  }, [eventToEdit, open, form, initialMode]);

  const handleDateSelect = (date: Date | undefined, onChange: (date: Date) => void) => {
    if (date) {
      onChange(date);
      setDatePickerOpen(false);
    }
  };

  const handleBatchDateSelect = (date: Date | undefined) => {
    if (date) {
      setBatchDate(date);
      setBatchDatePickerOpen(false);
    }
  };

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const { date, time, endTime, type, duration, notes } = values;

    if (eventToEdit && onEdit) {
      const [hours, minutes] = time.split(":").map(Number);
      const combinedDateTime = new Date(date);
      combinedDateTime.setHours(hours, minutes, 0, 0);

      let targetType = type;
      if (type === "nap") {
        targetType =
          eventToEdit.type === "nap_start" || eventToEdit.type === "nap_end"
            ? eventToEdit.type
            : "nap_start";
      }

      const eventData: Omit<NewSleepEvent, "childId"> = {
        time: combinedDateTime,
        type: targetType,
        notes: notes || "",
        duration: duration || 15,
      };

      onEdit(eventToEdit.id, eventData);
    } else {
      if (type === "nap" && endTime) {
        const [startHours, startMinutes] = time.split(":").map(Number);
        const [endHours, endMinutes] = endTime.split(":").map(Number);

        const startDateTime = new Date(date);
        startDateTime.setHours(startHours, startMinutes, 0, 0);

        const endDateTime = new Date(date);
        endDateTime.setHours(endHours, endMinutes, 0, 0);

        const calculatedDuration = calculateDurationMinutes(time, endTime);

        const napStartData: Omit<NewSleepEvent, "childId"> = {
          time: startDateTime,
          type: "nap_start",
          notes: notes || "",
          duration: calculatedDuration || duration || 15,
        };

        const napEndData: Omit<NewSleepEvent, "childId"> = {
          time: endDateTime,
          type: "nap_end",
          notes: notes || "",
          duration: calculatedDuration || duration || 15,
        };

        if (onSaveBatch) {
          onSaveBatch([napStartData, napEndData]);
        } else {
          onSave(napStartData);
          onSave(napEndData);
        }
      } else {
        const [hours, minutes] = time.split(":").map(Number);
        const combinedDateTime = new Date(date);
        combinedDateTime.setHours(hours, minutes, 0, 0);

        const eventData: Omit<NewSleepEvent, "childId"> = {
          time: combinedDateTime,
          type,
          notes: notes || "",
          duration: duration || 15,
        };

        onSave(eventData);
      }
    }

    setOpen(false);
  };

  const handleSaveAndAddAnother = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();
    const { date, time, endTime, duration, notes } = values;

    if (eventType === "nap" && endTime) {
      const [startHours, startMinutes] = time.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);

      const startDateTime = new Date(date);
      startDateTime.setHours(startHours, startMinutes, 0, 0);

      const endDateTime = new Date(date);
      endDateTime.setHours(endHours, endMinutes, 0, 0);

      const calculatedDuration = calculateDurationMinutes(time, endTime);

      const napStartData: Omit<NewSleepEvent, "childId"> = {
        time: startDateTime,
        type: "nap_start",
        notes: notes || "",
        duration: calculatedDuration || duration || 15,
      };

      const napEndData: Omit<NewSleepEvent, "childId"> = {
        time: endDateTime,
        type: "nap_end",
        notes: notes || "",
        duration: calculatedDuration || duration || 15,
      };

      if (onSaveBatch) {
        onSaveBatch([napStartData, napEndData]);
      } else {
        onSave(napStartData);
        onSave(napEndData);
      }

      // Compute next nap start: 2 hours 15 mins after previous nap ended
      const nextStartTotal = (endHours * 60 + endMinutes + 135) % (24 * 60);
      const nextStartH = Math.floor(nextStartTotal / 60);
      const nextStartM = (Math.round((nextStartTotal % 60) / 5) * 5) % 60;
      const nextStartTimeStr = `${pad(nextStartH)}:${pad(nextStartM)}`;
      const nextEndTimeStr = addMinutesToTimeString(nextStartTimeStr, 60);

      // Keep the current date!
      form.setValue("time", nextStartTimeStr);
      form.setValue("endTime", nextEndTimeStr);
      form.setValue("notes", "");

      setSavedFeedback("Nap saved! Set the time for your next nap below.");
    }
  };

  // Batch mode handlers
  const handleAddBatchNap = () => {
    const lastNap = batchNaps[batchNaps.length - 1];
    let nextStart = "16:30";
    if (lastNap && lastNap.endTime) {
      nextStart = addMinutesToTimeString(lastNap.endTime, 135);
    }
    const nextEnd = addMinutesToTimeString(nextStart, 45);
    setBatchNaps((prev) => [
      ...prev,
      {
        id: String(Date.now() + Math.random()),
        startTime: nextStart,
        endTime: nextEnd,
        notes: "",
      },
    ]);
  };

  const handleRemoveBatchNap = (id: string) => {
    if (batchNaps.length <= 1) return;
    setBatchNaps((prev) => prev.filter((nap) => nap.id !== id));
  };

  const handleUpdateBatchNap = (
    id: string,
    field: "startTime" | "endTime" | "notes",
    val: string
  ) => {
    setBatchNaps((prev) =>
      prev.map((nap) => {
        if (nap.id !== id) return nap;
        if (field === "startTime") {
          // If start time moves after end time, bump end time by 45m
          const curDuration = calculateDurationMinutes(val, nap.endTime);
          let newEndTime = nap.endTime;
          if (curDuration <= 0) {
            newEndTime = addMinutesToTimeString(val, 45);
          }
          return { ...nap, startTime: val, endTime: newEndTime };
        }
        return { ...nap, [field]: val };
      })
    );
  };

  const handleSaveBatchNaps = () => {
    // Validate each nap
    for (let i = 0; i < batchNaps.length; i++) {
      const nap = batchNaps[i];
      const duration = calculateDurationMinutes(nap.startTime, nap.endTime);
      if (duration <= 0) {
        setBatchError(`Nap ${i + 1}: End time must be after start time.`);
        return;
      }
    }
    setBatchError(null);

    const allEvents: Omit<NewSleepEvent, "childId">[] = [];

    batchNaps.forEach((nap, idx) => {
      const [startHours, startMinutes] = nap.startTime.split(":").map(Number);
      const [endHours, endMinutes] = nap.endTime.split(":").map(Number);

      const startDateTime = new Date(batchDate);
      startDateTime.setHours(startHours, startMinutes, 0, 0);

      const endDateTime = new Date(batchDate);
      endDateTime.setHours(endHours, endMinutes, 0, 0);

      const duration = calculateDurationMinutes(nap.startTime, nap.endTime);

      allEvents.push({
        time: startDateTime,
        type: "nap_start",
        notes: nap.notes || `Nap ${idx + 1}`,
        duration,
      });

      allEvents.push({
        time: endDateTime,
        type: "nap_end",
        notes: nap.notes || `Nap ${idx + 1}`,
        duration,
      });
    });

    if (onSaveBatch) {
      onSaveBatch(allEvents);
    } else {
      allEvents.forEach((evt) => onSave(evt));
    }

    setOpen(false);
  };

  const isFormToday = formDate ? isSameDay(formDate, new Date()) : false;
  const isFormYesterday = formDate ? isSameDay(formDate, subDays(new Date(), 1)) : false;

  const isBatchToday = isSameDay(batchDate, new Date());
  const isBatchYesterday = isSameDay(batchDate, subDays(new Date(), 1));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[480px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? "Edit Event"
              : mode === "batch"
              ? "Catch Up on Naps"
              : "Log Sleep Event"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the details of this sleep event."
              : mode === "batch"
              ? "Record multiple naps for a day all at once."
              : "Record a new sleep or feeding event for your child."}
          </DialogDescription>
        </DialogHeader>

        {/* Mode Switcher */}
        {!isEditing && (
          <div className="flex rounded-lg bg-muted p-1 text-muted-foreground">
            <button
              type="button"
              onClick={() => {
                setMode("single");
                setSavedFeedback(null);
                setBatchError(null);
              }}
              className={cn(
                "flex-1 rounded-md py-1.5 text-xs font-medium transition-all text-center touch-manipulation",
                mode === "single"
                  ? "bg-background text-foreground shadow-sm"
                  : "hover:text-foreground"
              )}
            >
              Single Event
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("batch");
                setSavedFeedback(null);
                setBatchError(null);
              }}
              className={cn(
                "flex-1 rounded-md py-1.5 text-xs font-medium transition-all flex items-center justify-center gap-1.5 touch-manipulation",
                mode === "batch"
                  ? "bg-background text-foreground shadow-sm"
                  : "hover:text-foreground"
              )}
            >
              <Layers className="h-3.5 w-3.5 text-primary" />
              <span>Catch Up on Naps</span>
            </button>
          </div>
        )}

        {/* BATCH NAP LOGGING MODE */}
        {mode === "batch" && !isEditing ? (
          <div className="space-y-4 pt-1">
            {/* Batch Date Selector with Quick Chips */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Date</label>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  type="button"
                  variant={isBatchToday ? "default" : "outline"}
                  size="sm"
                  onClick={() => setBatchDate(new Date())}
                  className={cn(
                    "h-8 text-xs font-medium touch-manipulation",
                    isBatchToday && "bg-primary text-primary-foreground"
                  )}
                >
                  Today
                </Button>
                <Button
                  type="button"
                  variant={isBatchYesterday ? "default" : "outline"}
                  size="sm"
                  onClick={() => setBatchDate(subDays(new Date(), 1))}
                  className={cn(
                    "h-8 text-xs font-medium touch-manipulation",
                    isBatchYesterday && "bg-primary text-primary-foreground"
                  )}
                >
                  Yesterday
                </Button>

                {isMobile && !isIOS ? (
                  <Input
                    type="date"
                    value={format(batchDate, "yyyy-MM-dd")}
                    onChange={(e) => {
                      if (e.target.value) {
                        setBatchDate(new Date(e.target.value));
                      }
                    }}
                    className="h-8 text-xs w-auto touch-manipulation"
                  />
                ) : (
                  <Popover
                    open={batchDatePickerOpen}
                    onOpenChange={setBatchDatePickerOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant={!isBatchToday && !isBatchYesterday ? "default" : "outline"}
                        size="sm"
                        className={cn(
                          "h-8 text-xs font-medium touch-manipulation",
                          !isBatchToday && !isBatchYesterday && "bg-primary text-primary-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                        {!isBatchToday && !isBatchYesterday
                          ? format(batchDate, "PP")
                          : "Other Date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      side="bottom"
                    >
                      <Calendar
                        mode="single"
                        selected={batchDate}
                        onSelect={handleBatchDateSelect}
                        disabled={(date) => date > new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            {/* List of Naps */}
            <div className="space-y-3">
              {batchNaps.map((nap, index) => {
                const duration = calculateDurationMinutes(nap.startTime, nap.endTime);
                return (
                  <div
                    key={nap.id}
                    className="p-3 rounded-lg border bg-card/60 space-y-3 relative"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">Nap {index + 1}</span>
                        {duration > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                            {formatDurationLabel(duration)}
                          </span>
                        )}
                      </div>
                      {batchNaps.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveBatchNap(nap.id)}
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive touch-manipulation"
                          aria-label={`Remove Nap ${index + 1}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
                      <div className="flex flex-col space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">
                          Start Time
                        </span>
                        <TimePicker
                          value={nap.startTime}
                          onChange={(newStart) =>
                            handleUpdateBatchNap(nap.id, "startTime", newStart)
                          }
                          showSteppers={true}
                        />
                      </div>
                      <div className="flex flex-col space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">
                          End Time
                        </span>
                        <TimePicker
                          value={nap.endTime}
                          onChange={(newEnd) =>
                            handleUpdateBatchNap(nap.id, "endTime", newEnd)
                          }
                          showSteppers={true}
                        />
                      </div>
                    </div>

                    <NapDurationPicker
                      startTime={nap.startTime}
                      endTime={nap.endTime}
                      onDurationSelect={(minutes) => {
                        const newEnd = addMinutesToTimeString(nap.startTime, minutes);
                        handleUpdateBatchNap(nap.id, "endTime", newEnd);
                      }}
                    />

                    <div>
                      <Input
                        placeholder="Notes (optional)"
                        value={nap.notes}
                        onChange={(e) =>
                          handleUpdateBatchNap(nap.id, "notes", e.target.value)
                        }
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                );
              })}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddBatchNap}
                className="w-full border-dashed gap-1.5 h-9 text-xs touch-manipulation"
              >
                <Plus className="h-4 w-4" />
                <span>Add Another Nap</span>
              </Button>
            </div>

            {batchError && (
              <div className="flex items-center gap-2 p-2.5 rounded-md bg-destructive/10 text-destructive text-xs font-medium">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{batchError}</span>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button
                type="button"
                onClick={handleSaveBatchNaps}
                className="w-full sm:w-auto"
              >
                Save All ({batchNaps.length}) Naps
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* SINGLE EVENT LOGGING MODE */
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-1">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Event Type</FormLabel>
                    <FormControl>
                      <ToggleGroup
                        type="single"
                        value={field.value}
                        onValueChange={(value) =>
                          value && field.onChange(value as SleepEventType)
                        }
                      >
                        {eventTypes.map(({ type, label, icon }) => (
                          <ToggleGroupItem
                            key={type}
                            value={type}
                            aria-label={label}
                            className="flex-col h-16"
                          >
                            {icon}
                            <span className="text-xs mt-1">{label}</span>
                          </ToggleGroupItem>
                        ))}
                      </ToggleGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Date Field with Quick Chips */}
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem className="flex flex-col space-y-1.5">
                    <FormLabel>Date</FormLabel>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Button
                        type="button"
                        variant={isFormToday ? "default" : "outline"}
                        size="sm"
                        onClick={() => field.onChange(new Date())}
                        className={cn(
                          "h-8 text-xs font-medium touch-manipulation",
                          isFormToday && "bg-primary text-primary-foreground"
                        )}
                      >
                        Today
                      </Button>
                      <Button
                        type="button"
                        variant={isFormYesterday ? "default" : "outline"}
                        size="sm"
                        onClick={() => field.onChange(subDays(new Date(), 1))}
                        className={cn(
                          "h-8 text-xs font-medium touch-manipulation",
                          isFormYesterday && "bg-primary text-primary-foreground"
                        )}
                      >
                        Yesterday
                      </Button>

                      {isMobile && !isIOS ? (
                        <FormControl>
                          <Input
                            type="date"
                            value={field.value ? format(field.value, "yyyy-MM-dd") : ""}
                            onChange={(e) => {
                              const date = e.target.value
                                ? new Date(e.target.value)
                                : new Date();
                              field.onChange(date);
                            }}
                            className="h-8 text-xs w-auto touch-manipulation"
                          />
                        </FormControl>
                      ) : (
                        <Popover
                          open={datePickerOpen}
                          onOpenChange={setDatePickerOpen}
                        >
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                type="button"
                                variant={
                                  !isFormToday && !isFormYesterday
                                    ? "default"
                                    : "outline"
                                }
                                size="sm"
                                className={cn(
                                  "h-8 text-xs font-medium touch-manipulation",
                                  !isFormToday &&
                                    !isFormYesterday &&
                                    "bg-primary text-primary-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                                {!isFormToday && !isFormYesterday && field.value
                                  ? format(field.value, "PP")
                                  : "Other Date"}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0"
                            align="start"
                            side="bottom"
                          >
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={(date) =>
                                handleDateSelect(date, field.onChange)
                              }
                              disabled={(date) => date > new Date()}
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Time fields */}
              {eventType === "nap" ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
                    <FormField
                      control={form.control}
                      name="time"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Start Time</FormLabel>
                          <FormControl>
                            <TimePicker
                              value={field.value}
                              onChange={(newStart) => {
                                field.onChange(newStart);
                                const curEndTime = form.getValues("endTime");
                                if (curEndTime) {
                                  const dur = calculateDurationMinutes(newStart, curEndTime);
                                  if (dur <= 0) {
                                    form.setValue(
                                      "endTime",
                                      addMinutesToTimeString(newStart, 45),
                                      { shouldValidate: true }
                                    );
                                  }
                                }
                              }}
                              showSteppers={true}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="endTime"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>End Time</FormLabel>
                          <FormControl>
                            <TimePicker
                              value={field.value}
                              onChange={field.onChange}
                              showSteppers={true}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Nap Duration Presets */}
                  <NapDurationPicker
                    startTime={formTime}
                    endTime={formEndTime || ""}
                    onDurationSelect={(minutes) => {
                      const curStart = form.getValues("time") || getRoundedTime();
                      const newEnd = addMinutesToTimeString(curStart, minutes);
                      form.setValue("endTime", newEnd, { shouldValidate: true });
                    }}
                  />
                </div>
              ) : (
                <FormField
                  control={form.control}
                  name="time"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Time</FormLabel>
                      <FormControl>
                        <TimePicker
                          value={field.value}
                          onChange={field.onChange}
                          showSteppers={true}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {eventType === "night_wake" && (
                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Wake Duration (minutes)</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Any notes about this event?"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {savedFeedback && (
                <div className="flex items-center gap-2 p-2.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                  <Check className="h-4 w-4 shrink-0" />
                  <span>{savedFeedback}</span>
                </div>
              )}

              <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between sm:space-x-0 pt-2">
                {!isEditing && eventType === "nap" && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSaveAndAddAnother}
                    className="w-full sm:w-auto text-xs font-medium touch-manipulation"
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Save & Add Another
                  </Button>
                )}
                <Button type="submit" className="w-full sm:w-auto text-xs font-medium">
                  {eventToEdit
                    ? "Save Changes"
                    : eventType === "nap"
                    ? "Log Nap"
                    : "Log Event"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
